import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { GroupBase, StylesConfig } from 'react-select';

import ExpandMore from '@mui/icons-material/ExpandMore';

import ErrorSnackBar from '../../../components/errors/ErrorSnackBar';
import { SearchableSelectType } from '../../../components/inputs/searchableSelect';
import LabelChip from '../../../components/labels/LabelChip';
import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';

import { EditorContext } from 'context/EditorContext';

import useGetComponentTypeIdByVariant from '../../../hooks/component-types/useGetComponentTypeIdByVariant';
import useQueryParams from '../../../hooks/helper/useQueryParams';
import useAddLabeltoVariantMutation from '../../../hooks/labels/useAddLabelToVariantMutation';
import useLabelCreateMutation from '../../../hooks/labels/useLabelCreateMutation';
import useLabelsQuery from '../../../hooks/labels/useLabelsQuery';

import { ParsedLabel } from '../../../models/label.model';
import { VariantWithLabel } from 'models/variant.model';

import LabelInfoDropdownSection from './LabelInfoDropdownSection';
import LabelsCreateAndSelectDropdown from './LabelsCreateAndSelectDropdown';

interface StyledProps {
    isInputOpen?: boolean;
    isEditable?: boolean;
}

const customStyleWithNegativeMargin: StylesConfig<
    SearchableSelectType,
    boolean,
    GroupBase<SearchableSelectType>
> = {
    control: (provided) => ({
        ...provided,
        border: `1px solid ${aampeTheme.colors.bordersWG01}`,
        borderColor: aampeTheme.colors.gray950,
        backgroundColor: aampeTheme.colors.white,
        outline: 'none',
        borderBottomLeftRadius: '0',
        borderBottomRightRadius: '0',
        boxShadow: 'none',
        '&:hover': {
            borderColor: aampeTheme.colors.bordersWG01,
            outline: 'none',
            boxShadow: 'none',
        },
        '&:focus': {
            border: 'none',
            outline: `1px solid ${aampeTheme.colors.bordersWG01}`,
            boxShadow: 'none',
        },
    }),
    menu: (provided) => ({
        ...provided,
        marginTop: '0px',
        minWidth: '120px',
        borderTopLeftRadius: '0px',
        borderTopRightRadius: '0px',
        maxHeight: '300px',
        overflowY: 'auto',
    }),
    menuList: (base) => ({
        ...base,
    }),
    input: (provided) => ({
        ...provided,
    }),
    group: (provided) => ({
        ...provided,
        padding: 0, // Reduce padding around the group container
    }),
    option: (provided, state) => ({
        ...provided,
        '&:hover': {
            backgroundColor: aampeTheme.colors.interactiveWG01,
        },
        backgroundColor: state.isSelected
            ? aampeTheme.colors.bordersWG01
            : aampeTheme.colors.white,
    }),
    singleValue: (provided) => ({
        ...provided,
        color: aampeTheme.colors.textAccessibleWG01,
        fontSize: aampeTheme.size.font.default,
        fontWeight: 400,
        lineHeight: '24px',
        padding: aampeTheme.spacing4(1, 2),
        borderRadius: aampeTheme.borderRadius.default,
        backgroundColor: aampeTheme.colors.backgroundWG01,
    }),
};

interface AlternateLabelSectionProps {
    data: VariantWithLabel;
    isEditable: boolean;
    footer?: React.ReactNode;
}

const AlternateLabelSection: React.FC<
    React.PropsWithChildren<AlternateLabelSectionProps>
> = ({ data, isEditable }) => {
    const { editorDetails } = useContext(EditorContext);
    const { formula } = editorDetails;

    const [isInputOpen, setIsInputOpen] = useState<boolean>(false);
    const [labelName, setLabelName] = useState<string>();

    const dropdownRef = useRef<HTMLDivElement>(null);

    const componentTypeId = useGetComponentTypeIdByVariant(data.variant_type);

    const { data: labels } = useLabelsQuery(componentTypeId as string);

    const params = useQueryParams();
    const labelsParam = params['labels'];
    const selectedLabels = useMemo(() => {
        return labelsParam ? JSON.parse(decodeURIComponent(labelsParam)) : [];
    }, [labelsParam]);

    const createLabelMutation = useLabelCreateMutation(
        data.variant_type,
        formula?.id,
        data.id,
    );
    const addLabelToVariantMutation = useAddLabeltoVariantMutation(
        formula?.id as number,
        data.component_id,
    );

    useEffect(() => {
        setLabelName(data.label?.label_name);
    }, [data]);

    const attachLabelToAlternate = (label_id: number | null) => {
        addLabelToVariantMutation.mutate(
            {
                id: data.id,
                data: { label_id },
            },
            {
                onError: () => setLabelName(data.label?.label_name),
            },
        );
    };

    const createAndAttachLabelToAlterate = (
        selectedLabel: SearchableSelectType,
    ) => {
        createLabelMutation.mutate(
            {
                label_name: selectedLabel.label,
                variant_type: data.variant_type,
            },
            {
                onSuccess: (res) => attachLabelToAlternate(res.data?.id),
                onSettled: () => {
                    setTimeout(createLabelMutation.reset, 3000);
                },
                onError: () => setLabelName(data.label?.label_name),
            },
        );
    };

    const handleLabelInputChange = (selectedLabel: SearchableSelectType) => {
        setIsInputOpen(false);
        setLabelName(selectedLabel.label);

        if (!selectedLabel.value) {
            attachLabelToAlternate(null);
            return;
        }

        if (labels?.find((item) => item.id === selectedLabel.value)) {
            attachLabelToAlternate(+selectedLabel.value);
            return;
        }

        createAndAttachLabelToAlterate(selectedLabel);
    };

    const groupedLabelOptions = React.useMemo(() => {
        if (!labels) return [];
        const miniSubListIds = new Set(
            selectedLabels.flatMap((label: ParsedLabel) =>
                label?.type === data?.variant_type ? label.ids.map(String) : [],
            ),
        );

        if (selectedLabels[0] && selectedLabels[0].type === data.variant_type) {
            selectedLabels[0].ids.forEach((id: string) =>
                miniSubListIds.add(id.toString()),
            );
        }

        if (selectedLabels[1] && selectedLabels[1].type === data.variant_type) {
            selectedLabels[1].ids.forEach((id: string) =>
                miniSubListIds.add(id.toString()),
            );
        }

        const options = labels.map((label) => ({
            label: label.label_name,
            value: label.id,
            isHighlighted: miniSubListIds.has(label.id.toString()),
        }));

        // Separate into two groups
        return [
            {
                label: 'Highlighted Labels',
                options: options.filter((option) => option.isHighlighted),
            },
            {
                label: 'Other Labels',
                options: options.filter((option) => !option.isHighlighted),
            },
        ];
    }, [labels, selectedLabels, data.variant_type]);

    const handleBlur = () => {
        document.body.style.overflow = 'auto';
    };
    const handleOpenDropdown = () => {
        setIsInputOpen(true);
        setTimeout(() => {
            if (dropdownRef.current) {
                const input = dropdownRef.current.querySelector('input');
                if (input) input.focus();
            }
        });
        document.body.style.overflow = 'hidden';
    };

    useEffect(() => {
        if (isInputOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
    }, [isInputOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsInputOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <Label
            isInputOpen={isInputOpen}
            onBlur={handleBlur}
            isEditable={isEditable}
        >
            {isInputOpen && isEditable ? (
                <div ref={dropdownRef}>
                    <LabelsCreateAndSelectDropdown
                        value={
                            labelName
                                ? {
                                      value: data.label?.id || '',
                                      label: labelName,
                                  }
                                : undefined
                        }
                        name={'label'}
                        options={groupedLabelOptions}
                        onChange={(field, value) =>
                            handleLabelInputChange(
                                value as SearchableSelectType,
                            )
                        }
                        placeholder={
                            groupedLabelOptions
                                ? 'Select an option or create one'
                                : 'Create a label'
                        }
                        data={data}
                        isCreatable
                        footer={<LabelInfoDropdownSection />}
                        customStyle={{
                            ...customStyleWithNegativeMargin,
                        }}
                        sortOption={false}
                    />
                </div>
            ) : labelName ? (
                <StyledChip title={labelName} onTagClick={handleOpenDropdown} />
            ) : (
                <>
                    {isEditable && (
                        <ChipContainer onClick={handleOpenDropdown}>
                            <ChipText>
                                <span>Select a label</span>
                                <ExpandMore />
                            </ChipText>
                        </ChipContainer>
                    )}
                </>
            )}

            {createLabelMutation.isError && (
                <ErrorSnackBar
                    title={(createLabelMutation.error as any)?.message}
                />
            )}
        </Label>
    );
};

const Label = styled('div').withConfig({
    shouldForwardProp: (prop) => !['isEditable', 'isInputOpen'].includes(prop), // Prevent forwarding these custom props
})<StyledProps>(({ isEditable, isInputOpen }) => ({
    padding: isEditable && !isInputOpen ? `${aampeTheme.spacing(0.5)}` : '0',
    '&:hover': {
        backgroundColor: aampeTheme.colors.interactiveWG01,
        borderRadius: aampeTheme.borderRadius.default,
    },
}));

const StyledChip = styled(LabelChip)({
    '& > span': {
        color: aampeTheme.colors.gray450,
        backgroundColor: aampeTheme.colors.gray150,
        stroke: aampeTheme.colors.bordersWG01,
    },
});

const ChipContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    maxWidth: '200px',
});
const ChipText = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(3),
    color: aampeTheme.colors.textAccessibleWG01,
    fontSize: aampeTheme.size.font.default,
    fontWeight: 400,
    lineHeight: '24px',
    width: '100%',
    padding: aampeTheme.spacing4(1, 2),
});

export default AlternateLabelSection;
